import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {PromotionListColConfig} from '@/services/MockData/Promotions/PromotionListColConfig'
import {downloadPromotionList, downloadPromotionTemplate, getPromotionList} from '@/services/promotionListService'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {useQuery} from '@tanstack/react-query'
import {useMemo, useState} from 'react'
import {useDataTable} from './useDataTable-hook'

export const usePromotionList = () => {
  const {
    setSortingObj,
    setSearchArry,
    pageIndex,
    currentPage,
    sortingObj,
    searchArr,
    onPageIndexChange,
    createSearchArray,
    resetAllBtnClicked,
  } = useDataTable()
  const [btnDisabled, setBtnDisabled] = useState(false)
  const formInitialData = {
    promotionTemplateName: '',
    storeSelector: '',
    actions: '',
    numberOfDays: '',
    numberOfPoints: '',
    schedule: '',
    startDate: '',
    endDate: '',
    mediaFile: [],
  }
  const [formData, setFormData] = useState({...formInitialData})

  // Memoized query parameters string for the API request
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    // Sorting
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('order', sortingObj.direction)
    } else {
      // Apply default sorting if no specific sorting is set
      params.append('sortBy', 'activeStartDate')
      params.append('order', 'DESC')
    }

    // Pagination - always include page and pageSize
    params.append('page', pageIndex.toString())
    params.append('pageSize', DEFAULT_TABLE_PAGE_SIZE.toString())

    return params.toString()
  }, [sortingObj, pageIndex])

  // Fetches promotion list data using react-query
  const {data: promotionListResult, isLoading} = useQuery({
    queryKey: ['PROMOTION_LIST', queryParams, searchArr],
    queryFn: () => {
      return getPromotionList(searchArr, queryParams)
    },
  })

  // Handler for the download button click
  const onDownloadBtnClick = async selectedIds => {
    if (btnDisabled) {
      return
    }
    setBtnDisabled(true)

    const payload = {}

    // Add 'ids' to payload if selectedIds is not empty
    if (selectedIds && selectedIds.length > 0) {
      payload.ids = selectedIds
    }

    // Add 'filters' to payload if searchArr is not empty
    if (searchArr && searchArr.length > 0) {
      payload.filters = searchArr
    }

    // Determine the actual body to send. If payload has no keys, send null. Otherwise, send payload.
    const requestBody = Object.keys(payload).length > 0 ? payload : null

    try {
      const response = await downloadPromotionList(requestBody, queryParams)
      downloadCSVFiles(response, `PromotionList_${getDateWithTimeString()}`)
      setBtnDisabled(false)
    } catch (error) {
      console.error('Download API call failed:', error)
      setBtnDisabled(false)
    }
  }

  const promotionListData = useMemo(() => {
    return promotionListResult || {}
  }, [promotionListResult])
  // Function to download the promotion template file
  const downloadPromoTemplate = async () => {
    const response = await downloadPromotionTemplate()

    const blob = new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = 'PromotionTemplate.xlsx'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    URL.revokeObjectURL(url)
  }

  return {
    promotionListData,
    promotionListColConfigData: PromotionListColConfig,
    currentPage,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    onPageIndexChange,
    isLoading,
    createSearchArray,
    resetAllBtnClicked,
    formData,
    setFormData,
    downloadPromoTemplate,
  }
}
